import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../lib/api';
import Sidebar from '../components/Sidebar';
import { toast } from 'sonner';
import {
  Users, Briefcase, DollarSign, TrendingUp, Plus, RefreshCw, Loader2,
  MapPin, CheckCircle2, Clock, XCircle, ChevronRight, Send, ArrowLeft,
  UserPlus, Building2, Calculator, Globe, Search, ExternalLink, Filter,
  Trophy, Medal, Crown, Award, CreditCard, Sparkles, Zap, Star, Receipt,
  Upload, FileText, Shield, Target
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';

const STATUS_BADGE = {
  submitted: 'bg-blue-100 text-blue-700', interviewing: 'bg-amber-100 text-amber-700',
  hired: 'bg-emerald-100 text-emerald-700', rejected: 'bg-red-100 text-red-700',
  active: 'bg-emerald-100 text-emerald-700', pending: 'bg-amber-100 text-amber-700',
  approved: 'bg-emerald-100 text-emerald-700', completed: 'bg-blue-100 text-blue-700',
};

export default function TalentHub() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('Talent Hub');
  const [view, setView] = useState('jobs');
  const [jobs, setJobs] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [engagements, setEngagements] = useState([]);
  const [hireRequests, setHireRequests] = useState([]);
  const [revenue, setRevenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateJob, setShowCreateJob] = useState(false);
  const [showRefer, setShowRefer] = useState(null);
  const [showHireModal, setShowHireModal] = useState(null);
  const [showEngagement, setShowEngagement] = useState(false);
  const [jobForm, setJobForm] = useState({ title: '', company: '', description: '', location: '', remote: false, salary_min: '', salary_max: '', referral_bounty: '', industry: 'technology', employment_type: 'full_time', requirements: '' });
  const [referForm, setReferForm] = useState({ candidate_name: '', candidate_email: '', candidate_phone: '', candidate_linkedin: '', notes: '', relationship: '' });
  const [hireForm, setHireForm] = useState({ proposed_salary: '' });
  const [engForm, setEngForm] = useState({ freelancer_name: '', freelancer_email: '', role: '', hourly_rate: '', business_name: '' });
  const [feeCalc, setFeeCalc] = useState(null);
  const [externalJobs, setExternalJobs] = useState([]);
  const [externalSources, setExternalSources] = useState([]);
  const [extSearch, setExtSearch] = useState('');
  const [extSourceFilter, setExtSourceFilter] = useState('all');
  const [extLoading, setExtLoading] = useState(false);
  const [showExtRefer, setShowExtRefer] = useState(null);
  const [extReferForm, setExtReferForm] = useState({ candidate_name: '', candidate_email: '', candidate_phone: '', candidate_linkedin: '', notes: '', relationship: '' });
  const [leaderboard, setLeaderboard] = useState([]);
  const [lbPeriod, setLbPeriod] = useState('all');
  const [lbLoading, setLbLoading] = useState(false);
  const [payouts, setPayouts] = useState([]);
  const [aiMatch, setAiMatch] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [freelancerProfile, setFreelancerProfile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [bulkMatches, setBulkMatches] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [allProfiles, setAllProfiles] = useState([]);
  const isAdmin = user?.role === 'platform_admin' || user?.role === 'business_admin';

  const fetchData = useCallback(async () => {
    try {
      const promises = [
        api.get('/api/talent/jobs'),
        api.get('/api/talent/referrals'),
        api.get('/api/talent/earnings'),
        api.get('/api/talent/payouts'),
      ];
      if (isAdmin) {
        promises.push(api.get('/api/talent/engagements'), api.get('/api/talent/hire-requests'));
      }
      if (user?.role === 'platform_admin') {
        promises.push(api.get('/api/talent/revenue'));
      }
      const results = await Promise.all(promises);
      setJobs(results[0].data.jobs || []);
      setReferrals(results[1].data.referrals || []);
      setEarnings(results[2].data);
      setPayouts(results[3].data.payouts || []);
      if (isAdmin && results[4]) setEngagements(results[4].data.engagements || []);
      if (isAdmin && results[5]) setHireRequests(results[5].data.hire_requests || []);
      if (user?.role === 'platform_admin' && results[6]) setRevenue(results[6].data);
      // Auto-seed demo data
      if (results[0].data.jobs.length === 0) {
        await api.post('/api/talent/seed-demo');
        const { data } = await api.get('/api/talent/jobs');
        setJobs(data.jobs || []);
      }
    } catch (e) { /* handled */ } finally { setLoading(false); }
  }, [isAdmin, user?.role]);

  useEffect(() => { fetchData(); }, [fetchData]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/api/talent/freelancer/profile');
        setFreelancerProfile(data.profile);
        if (isAdmin) {
          const { data: pd } = await api.get('/api/talent/freelancer/profiles');
          setAllProfiles(pd.profiles || []);
        }
      } catch (e) { /* handled */ }
    })();
  }, [isAdmin]);

  const fetchExternalJobs = useCallback(async (search = '', source = 'all') => {
    setExtLoading(true);
    try {
      const params = new URLSearchParams({ limit: '50' });
      if (search) params.set('search', search);
      if (source && source !== 'all') params.set('source', source);
      const [jobsRes, sourcesRes] = await Promise.all([
        api.get(`/api/talent/external-jobs?${params}`),
        api.get('/api/talent/external-sources'),
      ]);
      setExternalJobs(jobsRes.data.jobs || []);
      setExternalSources(sourcesRes.data.sources || []);
    } catch { toast.error('Failed to load external jobs'); } finally { setExtLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (view === 'external') fetchExternalJobs(extSearch, extSourceFilter); }, [view, fetchExternalJobs]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchLeaderboard = useCallback(async (period = 'all') => {
    setLbLoading(true);
    try {
      const { data } = await api.get(`/api/talent/leaderboard?period=${period}`);
      setLeaderboard(data.leaderboard || []);
    } catch (e) { /* handled */ } finally { setLbLoading(false); }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { if (view === 'leaderboard') fetchLeaderboard(lbPeriod); }, [view, lbPeriod, fetchLeaderboard]);

  const submitExtReferral = async (job) => {
    if (!extReferForm.candidate_name || !extReferForm.candidate_email) { toast.error('Name and email required'); return; }
    try {
      await api.post('/api/talent/external-referrals', {
        ...extReferForm,
        external_job_id: job.id,
        job_title: job.title,
        company: job.company,
        source: job.source,
        external_url: job.url,
        referral_bounty: job.referral_bounty,
      });
      toast.success('Referral submitted for external job!');
      setShowExtRefer(null);
      setExtReferForm({ candidate_name: '', candidate_email: '', candidate_phone: '', candidate_linkedin: '', notes: '', relationship: '' });
      fetchData();
    } catch { toast.error('Referral failed'); }
  };

  const createJob = async () => {
    if (!jobForm.title || !jobForm.company) { toast.error('Title and company required'); return; }
    try {
      const payload = { ...jobForm, salary_min: Number(jobForm.salary_min) || 0, salary_max: Number(jobForm.salary_max) || 0, referral_bounty: Number(jobForm.referral_bounty) || 0, requirements: jobForm.requirements.split(',').map(s => s.trim()).filter(Boolean) };
      await api.post('/api/talent/jobs', payload);
      toast.success('Job posted!');
      setShowCreateJob(false);
      setJobForm({ title: '', company: '', description: '', location: '', remote: false, salary_min: '', salary_max: '', referral_bounty: '', industry: 'technology', employment_type: 'full_time', requirements: '' });
      fetchData();
    } catch { toast.error('Failed to post job'); }
  };

  const submitReferral = async () => {
    if (!referForm.candidate_name || !referForm.candidate_email) { toast.error('Name and email required'); return; }
    try {
      await api.post('/api/talent/referrals', { ...referForm, job_id: showRefer });
      toast.success('Referral submitted!');
      setShowRefer(null);
      setReferForm({ candidate_name: '', candidate_email: '', candidate_phone: '', candidate_linkedin: '', notes: '', relationship: '' });
      fetchData();
    } catch { toast.error('Referral failed'); }
  };

  const updateReferralStatus = async (id, status) => {
    try {
      await api.put(`/api/talent/referrals/${id}`, { status });
      toast.success(`Referral ${status}`);
      fetchData();
    } catch { toast.error('Update failed'); }
  };

  const requestHire = async (engagementId) => {
    if (!hireForm.proposed_salary) { toast.error('Enter proposed salary'); return; }
    try {
      await api.post('/api/talent/hire-request', { engagement_id: engagementId, proposed_salary: Number(hireForm.proposed_salary) });
      toast.success('Hire request submitted!');
      setShowHireModal(null);
      setHireForm({ proposed_salary: '' });
      fetchData();
    } catch { toast.error('Request failed'); }
  };

  const calculateFee = async (salary) => {
    try {
      const { data } = await api.post('/api/talent/calculate-fee', { salary: Number(salary) });
      setFeeCalc(data);
    } catch (e) { /* handled */ }
  };

  const createEngagement = async () => {
    if (!engForm.freelancer_name) { toast.error('Freelancer name required'); return; }
    try {
      await api.post('/api/talent/engagements', { ...engForm, hourly_rate: Number(engForm.hourly_rate) || 0 });
      toast.success('Engagement tracked!');
      setShowEngagement(false);
      setEngForm({ freelancer_name: '', freelancer_email: '', role: '', hourly_rate: '', business_name: '' });
      fetchData();
    } catch { toast.error('Failed'); }
  };

  const payConversionFee = async (hireRequestId) => {
    try {
      const { data } = await api.post('/api/talent/invoice/conversion-fee', { hire_request_id: hireRequestId, origin_url: window.location.origin });
      if (data.url) { window.location.href = data.url; }
      else { toast.info(data.message || 'Invoice already generated'); }
    } catch { toast.error('Invoice generation failed'); }
  };

  const approveBountyPayout = async (referralId) => {
    try {
      await api.post('/api/talent/invoice/referral-bounty', { referral_id: referralId, origin_url: window.location.origin });
      toast.success('Bounty payout approved!');
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || 'Payout failed'); }
  };

  const runAiMatch = async (job) => {
    setAiLoading(true);
    setAiMatch(null);
    try {
      const { data } = await api.post('/api/talent/ai-match', {
        job_title: job.title,
        requirements: job.requirements || [],
        description: job.description || '',
        candidate_name: referForm.candidate_name,
        notes: referForm.notes,
        candidate_linkedin: referForm.candidate_linkedin,
        relationship: referForm.relationship,
      });
      setAiMatch(data);
    } catch { toast.error('AI matching failed'); }
    finally { setAiLoading(false); }
  };

  const uploadResume = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.match(/\.(pdf|docx|doc)$/i)) { toast.error('Only PDF and DOCX files'); return; }
    if (file.size > 10 * 1024 * 1024) { toast.error('File too large (max 10MB)'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await api.post('/api/talent/freelancer/onboard', formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setFreelancerProfile(data.profile);
      toast.success(data.ai_parsed ? 'Resume parsed with AI!' : 'Resume uploaded!');
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(false); }
  };

  const runBulkMatch = async () => {
    setBulkLoading(true);
    setBulkMatches(null);
    try {
      const { data } = await api.post('/api/talent/freelancer/bulk-match', {});
      setBulkMatches(data);
    } catch (err) { toast.error(err.response?.data?.detail || 'Matching failed'); }
    finally { setBulkLoading(false); }
  };

  const verifyFreelancer = async (userId, status) => {
    try {
      await api.put(`/api/talent/freelancer/verify/${userId}`, { status });
      toast.success(`Freelancer ${status}`);
      const { data } = await api.get('/api/talent/freelancer/profiles');
      setAllProfiles(data.profiles || []);
    } catch { toast.error('Verification failed'); }
  };

  if (loading) return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></main>
    </div>
  );

  return (
    <div className="flex min-h-screen bg-gray-50" data-testid="talent-hub-page">
      <Sidebar activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 p-6 md:p-8 lg:p-10 overflow-y-auto">
        <div className="space-y-6 animate-fade-in">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="font-heading text-2xl font-bold tracking-tight text-gray-900 flex items-center gap-2">
                <Briefcase className="w-6 h-6 text-[#D4A843]" /> Talent Hub
              </h1>
              <p className="text-sm text-gray-500 mt-1">Refer candidates, earn bounties, and manage freelancer-to-hire conversions</p>
            </div>
            <Button variant="outline" size="sm" onClick={fetchData} className="rounded-lg text-xs"><RefreshCw className="w-3.5 h-3.5 mr-1" /> Refresh</Button>
          </div>

          {/* Sub-nav */}
          <div className="flex gap-1 border-b border-gray-200 pb-0">
            {[
              { k: 'onboard', l: 'Onboard' },
              { k: 'jobs', l: 'Job Board' },
              { k: 'external', l: 'External Jobs' },
              { k: 'referrals', l: 'My Referrals' },
              { k: 'earnings', l: 'Earnings' },
              { k: 'payouts', l: 'Payouts' },
              { k: 'leaderboard', l: 'Leaderboard' },
              ...(isAdmin ? [{ k: 'freelancers', l: 'Freelancers' }, { k: 'engagements', l: 'Engagements' }, { k: 'hire_requests', l: 'Hire Requests' }] : []),
              ...(user?.role === 'platform_admin' ? [{ k: 'revenue', l: 'Revenue' }] : []),
            ].map(t => (
              <button key={t.k} onClick={() => setView(t.k)} className={`px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${view === t.k ? 'border-[#D4A843] text-[#D4A843]' : 'border-transparent text-gray-500 hover:text-gray-700'}`} data-testid={`talent-tab-${t.k}`}>{t.l}</button>
            ))}
          </div>

          {/* ── ONBOARD (Resume Upload) ── */}
          {view === 'onboard' && (
            <div className="space-y-4" data-testid="talent-onboard">
              <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Shield className="w-4 h-4 text-[#D4A843]" /> Freelancer Onboarding</h2>
              <p className="text-xs text-gray-500">Resume upload is <span className="font-bold text-red-600">required</span> to onboard as a freelancer. Your resume is AI-parsed to build your verified profile.</p>

              {!freelancerProfile ? (
                <div className="bg-white border-2 border-dashed border-gray-300 rounded-xl p-10 text-center hover:border-[#D4A843]/50 transition-colors">
                  <Upload className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-sm font-bold text-gray-900 mb-1">Upload Your Resume</p>
                  <p className="text-xs text-gray-400 mb-4">PDF or DOCX (max 10MB). AI will extract your skills, experience, and certifications.</p>
                  <label className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs font-medium cursor-pointer transition-colors" data-testid="resume-upload-btn">
                    {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                    {uploading ? 'Parsing...' : 'Choose File'}
                    <input type="file" accept=".pdf,.docx,.doc" onChange={uploadResume} className="hidden" data-testid="resume-file-input" />
                  </label>
                </div>
              ) : (
                <div className="space-y-4 animate-fade-in">
                  {/* Verification Badge */}
                  <div className={`flex items-center gap-2 px-4 py-2 rounded-lg text-xs font-bold ${freelancerProfile.verification_status === 'verified' ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : freelancerProfile.verification_status === 'rejected' ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-amber-50 text-amber-700 border border-amber-200'}`} data-testid="verification-badge">
                    {freelancerProfile.verification_status === 'verified' ? <CheckCircle2 className="w-4 h-4" /> : freelancerProfile.verification_status === 'rejected' ? <XCircle className="w-4 h-4" /> : <Clock className="w-4 h-4" />}
                    {freelancerProfile.verification_status === 'verified' ? 'Verified Freelancer' : freelancerProfile.verification_status === 'rejected' ? 'Profile Rejected' : 'Pending Verification'}
                  </div>

                  {/* Profile Card */}
                  <div className="bg-white border border-gray-200 rounded-lg p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">{freelancerProfile.name}</h3>
                        <p className="text-sm text-[#D4A843] font-medium">{freelancerProfile.title}</p>
                        <p className="text-xs text-gray-400 mt-0.5">{freelancerProfile.email} | {freelancerProfile.phone} | {freelancerProfile.location}</p>
                        {freelancerProfile.linkedin && <p className="text-xs text-gray-400">{freelancerProfile.linkedin}</p>}
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] text-gray-400">Resume: {freelancerProfile.resume_filename}</p>
                        <label className="text-[10px] text-[#D4A843] cursor-pointer hover:underline">
                          Re-upload
                          <input type="file" accept=".pdf,.docx,.doc" onChange={uploadResume} className="hidden" />
                        </label>
                      </div>
                    </div>
                    {freelancerProfile.summary && <p className="text-sm text-gray-600 mb-4">{freelancerProfile.summary}</p>}

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <p className="text-xs font-bold text-gray-900 mb-2">Skills ({freelancerProfile.skills?.length || 0})</p>
                        <div className="flex flex-wrap gap-1">{freelancerProfile.skills?.map((s, i) => <span key={`item-${i}`} className="text-[10px] bg-[#D4A843]/10 text-[#D4A843] px-2 py-0.5 rounded font-medium">{s}</span>)}</div>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-900 mb-2">Certifications</p>
                        <div className="flex flex-wrap gap-1">{freelancerProfile.certifications?.map((c, i) => <span key={`item-${i}`} className="text-[10px] bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded font-medium">{c}</span>)}</div>
                      </div>
                      <div>
                        <p className="text-xs font-bold text-gray-900 mb-2">Experience</p>
                        <p className="text-sm font-bold text-gray-900">{freelancerProfile.years_experience} years</p>
                        <div className="space-y-1 mt-1">{freelancerProfile.experience?.slice(0, 3).map((e, i) => <p key={`item-${i}`} className="text-[10px] text-gray-500">{e.title} @ {e.company}</p>)}</div>
                      </div>
                    </div>
                  </div>

                  {/* Bulk Match */}
                  <div className="bg-white border border-gray-200 rounded-lg p-5">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="text-xs font-bold text-gray-900 uppercase tracking-wider flex items-center gap-1"><Target className="w-3.5 h-3.5 text-[#D4A843]" /> AI Job Matching</h3>
                      <Button size="sm" onClick={runBulkMatch} disabled={bulkLoading} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="bulk-match-btn">
                        {bulkLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />} Match Against All Jobs
                      </Button>
                    </div>
                    {bulkMatches && (
                      <div className="space-y-2 animate-fade-in">
                        {bulkMatches.top_recommendation && <p className="text-xs text-gray-600 bg-violet-50 p-3 rounded-lg border border-violet-200"><Sparkles className="w-3 h-3 inline text-violet-600 mr-1" />{bulkMatches.top_recommendation}</p>}
                        {bulkMatches.matches?.map((m, i) => (
                          <div key={m.job_id} className={`flex items-center justify-between p-3 rounded-lg border ${m.score >= 75 ? 'bg-emerald-50 border-emerald-200' : m.score >= 50 ? 'bg-white border-gray-200' : 'bg-gray-50 border-gray-200'}`} data-testid={`match-${i}`}>
                            <div className="flex items-center gap-3">
                              <span className={`text-lg font-bold ${m.score >= 75 ? 'text-emerald-600' : m.score >= 50 ? 'text-amber-600' : 'text-gray-400'}`}>#{i + 1}</span>
                              <div>
                                <p className="text-sm font-bold text-gray-900">{m.job_title} <span className="text-[10px] text-gray-400 font-normal">@ {m.company}</span></p>
                                <p className="text-[10px] text-gray-500">{m.reasons?.[0]}</p>
                              </div>
                            </div>
                            <div className="flex items-center gap-3">
                              {m.referral_bounty > 0 && <span className="text-[10px] text-emerald-600 font-bold">${m.referral_bounty} bounty</span>}
                              <span className={`px-2 py-0.5 rounded text-xs font-bold ${m.score >= 75 ? 'bg-emerald-100 text-emerald-700' : m.score >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-gray-200 text-gray-600'}`}>{m.score}%</span>
                              <span className={`text-[10px] font-bold ${m.rating === 'Excellent' ? 'text-emerald-600' : m.rating === 'Strong' ? 'text-blue-600' : 'text-gray-500'}`}>{m.rating}</span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* ── FREELANCERS (Admin) ── */}
          {view === 'freelancers' && isAdmin && (
            <div className="space-y-4" data-testid="talent-freelancers">
              <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Users className="w-4 h-4 text-[#D4A843]" /> Freelancer Profiles ({allProfiles.length})</h2>
              {allProfiles.length === 0 ? (
                <p className="text-center py-8 text-gray-400 bg-white rounded-lg border border-gray-200">No freelancers onboarded yet.</p>
              ) : (
                <div className="space-y-3">
                  {allProfiles.map(p => (
                    <div key={p.user_id} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`freelancer-${p.user_id}`}>
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="text-sm font-bold text-gray-900">{p.name} <span className="text-xs text-gray-400 font-normal">({p.email})</span></p>
                          <p className="text-xs text-[#D4A843]">{p.title}</p>
                          <p className="text-[10px] text-gray-400">{p.years_experience}yr exp | {p.skills?.length || 0} skills | {p.location}</p>
                          <div className="flex flex-wrap gap-1 mt-1">{p.skills?.slice(0, 8).map((s, i) => <span key={`item-${i}`} className="text-[10px] bg-gray-100 px-1.5 py-0.5 rounded">{s}</span>)}</div>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${p.verification_status === 'verified' ? 'bg-emerald-100 text-emerald-700' : p.verification_status === 'rejected' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>{p.verification_status}</span>
                          {p.verification_status === 'pending' && (
                            <div className="flex gap-1">
                              <Button size="sm" className="bg-emerald-600 text-white rounded text-[10px] h-7 px-2" onClick={() => verifyFreelancer(p.user_id, 'verified')} data-testid={`verify-${p.user_id}`}>Verify</Button>
                              <Button size="sm" variant="outline" className="text-red-500 rounded text-[10px] h-7 px-2" onClick={() => verifyFreelancer(p.user_id, 'rejected')}>Reject</Button>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── JOB BOARD ── */}
          {view === 'jobs' && (
            <div className="space-y-4" data-testid="talent-jobs">
              {isAdmin && (
                <Button onClick={() => setShowCreateJob(!showCreateJob)} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="talent-post-job"><Plus className="w-3.5 h-3.5 mr-1" /> Post Job</Button>
              )}
              {showCreateJob && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-3 animate-fade-in" data-testid="talent-job-form">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div><label className="text-xs font-medium text-gray-700">Title *</label><Input value={jobForm.title} onChange={e => setJobForm({...jobForm, title: e.target.value})} className="mt-1 rounded-lg" data-testid="talent-job-title" /></div>
                    <div><label className="text-xs font-medium text-gray-700">Company *</label><Input value={jobForm.company} onChange={e => setJobForm({...jobForm, company: e.target.value})} className="mt-1 rounded-lg" data-testid="talent-job-company" /></div>
                    <div><label className="text-xs font-medium text-gray-700">Location</label><Input value={jobForm.location} onChange={e => setJobForm({...jobForm, location: e.target.value})} className="mt-1 rounded-lg" data-testid="talent-job-location" /></div>
                    <div><label className="text-xs font-medium text-gray-700">Salary Min</label><Input type="number" value={jobForm.salary_min} onChange={e => setJobForm({...jobForm, salary_min: e.target.value})} className="mt-1 rounded-lg" data-testid="talent-job-salmin" /></div>
                    <div><label className="text-xs font-medium text-gray-700">Salary Max</label><Input type="number" value={jobForm.salary_max} onChange={e => setJobForm({...jobForm, salary_max: e.target.value})} className="mt-1 rounded-lg" data-testid="talent-job-salmax" /></div>
                    <div><label className="text-xs font-medium text-gray-700">Referral Bounty ($)</label><Input type="number" value={jobForm.referral_bounty} onChange={e => setJobForm({...jobForm, referral_bounty: e.target.value})} className="mt-1 rounded-lg" data-testid="talent-job-bounty" /></div>
                    <div><label className="text-xs font-medium text-gray-700">Industry</label><select value={jobForm.industry} onChange={e => setJobForm({...jobForm, industry: e.target.value})} className="mt-1 w-full h-9 rounded-lg border border-gray-200 px-3 text-xs bg-white"><option value="technology">Technology</option><option value="cannabis">Cannabis</option><option value="healthcare">Healthcare</option><option value="manufacturing">Manufacturing</option><option value="financial">Financial</option><option value="other">Other</option></select></div>
                    <div className="md:col-span-2"><label className="text-xs font-medium text-gray-700">Requirements (comma-sep)</label><Input value={jobForm.requirements} onChange={e => setJobForm({...jobForm, requirements: e.target.value})} className="mt-1 rounded-lg" placeholder="CCNA, 5 years, Python" data-testid="talent-job-reqs" /></div>
                  </div>
                  <div><label className="text-xs font-medium text-gray-700">Description</label><textarea value={jobForm.description} onChange={e => setJobForm({...jobForm, description: e.target.value})} className="w-full mt-1 h-20 rounded-lg border border-gray-200 p-3 text-sm resize-none" data-testid="talent-job-desc" /></div>
                  <Button onClick={createJob} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="talent-job-submit">Post Job</Button>
                </div>
              )}
              <div className="space-y-3">
                {jobs.map(job => (
                  <div key={job.id} className="bg-white border border-gray-200 rounded-lg p-5 hover-lift" data-testid={`talent-job-${job.id}`}>
                    <div className="flex items-start justify-between">
                      <div>
                        <h3 className="text-sm font-bold text-gray-900">{job.title}</h3>
                        <p className="text-xs text-gray-500 flex items-center gap-2 mt-1">
                          <Building2 className="w-3 h-3" /> {job.company}
                          <MapPin className="w-3 h-3 ml-1" /> {job.location || 'Not specified'} {job.remote && <span className="text-emerald-600 font-medium">Remote</span>}
                        </p>
                      </div>
                      <div className="text-right">
                        {job.referral_bounty > 0 && <p className="text-lg font-bold text-emerald-600" data-testid={`talent-bounty-${job.id}`}>${job.referral_bounty.toLocaleString()}<span className="text-[10px] text-gray-400 font-normal"> bounty</span></p>}
                        {job.salary_min > 0 && <p className="text-[10px] text-gray-400">${(job.salary_min/1000).toFixed(0)}k — ${(job.salary_max/1000).toFixed(0)}k</p>}
                      </div>
                    </div>
                    {job.requirements?.length > 0 && <div className="flex flex-wrap gap-1 mt-2">{job.requirements.map((r, i) => <span key={`item-${i}`} className="text-[10px] bg-gray-100 px-2 py-0.5 rounded">{r}</span>)}</div>}
                    <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-3 text-[10px] text-gray-400">
                        <span>{job.referrals} referrals</span>
                        <span>{job.hires} hires</span>
                        <span className="capitalize">{job.industry}</span>
                      </div>
                      <Button size="sm" className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" onClick={() => setShowRefer(job.id)} data-testid={`talent-refer-${job.id}`}><UserPlus className="w-3 h-3 mr-1" /> Refer Candidate</Button>
                    </div>
                    {/* Refer Modal */}
                    {showRefer === job.id && (
                      <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200 animate-fade-in" data-testid={`talent-refer-form-${job.id}`}>
                        <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">Refer a Candidate for: {job.title}</h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <div><label className="text-xs text-gray-600">Name *</label><Input value={referForm.candidate_name} onChange={e => setReferForm({...referForm, candidate_name: e.target.value})} className="mt-1 rounded-lg" data-testid="refer-name" /></div>
                          <div><label className="text-xs text-gray-600">Email *</label><Input value={referForm.candidate_email} onChange={e => setReferForm({...referForm, candidate_email: e.target.value})} className="mt-1 rounded-lg" data-testid="refer-email" /></div>
                          <div><label className="text-xs text-gray-600">Phone</label><Input value={referForm.candidate_phone} onChange={e => setReferForm({...referForm, candidate_phone: e.target.value})} className="mt-1 rounded-lg" data-testid="refer-phone" /></div>
                          <div><label className="text-xs text-gray-600">LinkedIn</label><Input value={referForm.candidate_linkedin} onChange={e => setReferForm({...referForm, candidate_linkedin: e.target.value})} className="mt-1 rounded-lg font-mono text-xs" data-testid="refer-linkedin" /></div>
                          <div><label className="text-xs text-gray-600">Relationship</label><select value={referForm.relationship} onChange={e => setReferForm({...referForm, relationship: e.target.value})} className="mt-1 w-full h-9 rounded-lg border border-gray-200 px-3 text-xs bg-white" data-testid="refer-relationship"><option value="">Select...</option><option value="colleague">Former Colleague</option><option value="friend">Friend</option><option value="network">Professional Network</option><option value="other">Other</option></select></div>
                          <div><label className="text-xs text-gray-600">Notes</label><Input value={referForm.notes} onChange={e => setReferForm({...referForm, notes: e.target.value})} className="mt-1 rounded-lg" placeholder="Why they're a good fit..." data-testid="refer-notes" /></div>
                        </div>
                        <div className="flex gap-2 mt-3">
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs" onClick={submitReferral} data-testid="refer-submit"><Send className="w-3 h-3 mr-1" /> Submit Referral</Button>
                          {referForm.candidate_name && (
                            <Button size="sm" variant="outline" className="text-xs rounded-lg border-violet-300 text-violet-600 hover:bg-violet-50" onClick={() => runAiMatch(job)} disabled={aiLoading} data-testid="ai-match-btn">
                              {aiLoading ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Sparkles className="w-3 h-3 mr-1" />} AI Match Score
                            </Button>
                          )}
                          <Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={() => { setShowRefer(null); setAiMatch(null); }}>Cancel</Button>
                        </div>
                        {/* AI Match Result */}
                        {aiMatch && (
                          <div className="mt-3 p-4 rounded-lg border border-violet-200 bg-violet-50 animate-fade-in" data-testid="ai-match-result">
                            <div className="flex items-center justify-between mb-3">
                              <h5 className="text-xs font-bold text-violet-800 uppercase tracking-wider flex items-center gap-1"><Sparkles className="w-3.5 h-3.5" /> AI Match Analysis</h5>
                              <div className="flex items-center gap-2">
                                <span className={`text-2xl font-bold ${aiMatch.score >= 75 ? 'text-emerald-600' : aiMatch.score >= 50 ? 'text-amber-600' : 'text-red-500'}`}>{aiMatch.score}</span>
                                <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${aiMatch.score >= 75 ? 'bg-emerald-100 text-emerald-700' : aiMatch.score >= 50 ? 'bg-amber-100 text-amber-700' : 'bg-red-100 text-red-700'}`}>{aiMatch.rating}</span>
                              </div>
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 text-xs">
                              <div><p className="font-bold text-emerald-700 mb-1">Strengths</p>{aiMatch.strengths?.map((s, i) => <p key={`item-${i}`} className="text-gray-600 flex items-center gap-1"><CheckCircle2 className="w-3 h-3 text-emerald-500 flex-shrink-0" /> {s}</p>)}</div>
                              <div><p className="font-bold text-amber-700 mb-1">Gaps</p>{aiMatch.gaps?.map((g, i) => <p key={`item-${i}`} className="text-gray-600 flex items-center gap-1"><XCircle className="w-3 h-3 text-amber-500 flex-shrink-0" /> {g}</p>)}</div>
                              <div><p className="font-bold text-[#D4A843] mb-1">Talking Points</p>{aiMatch.talking_points?.map((t, i) => <p key={`item-${i}`} className="text-gray-600 flex items-center gap-1"><Zap className="w-3 h-3 text-[#D4A843] flex-shrink-0" /> {t}</p>)}</div>
                            </div>
                            {aiMatch.recommendation && <p className="mt-2 text-xs text-gray-700 bg-white p-2 rounded border border-gray-200"><strong>Recommendation:</strong> {aiMatch.recommendation}</p>}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* ── EXTERNAL JOBS ── */}
          {view === 'external' && (
            <div className="space-y-4" data-testid="talent-external-jobs">
              {/* Search & Filter Bar */}
              <div className="flex flex-col sm:flex-row gap-3">
                <div className="flex-1 relative">
                  <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input
                    value={extSearch}
                    onChange={e => setExtSearch(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && fetchExternalJobs(extSearch, extSourceFilter)}
                    placeholder="Search external jobs (e.g. engineer, python, devops)..."
                    className="pl-10 rounded-lg"
                    data-testid="ext-search-input"
                  />
                </div>
                <div className="flex gap-2">
                  <select
                    value={extSourceFilter}
                    onChange={e => { setExtSourceFilter(e.target.value); fetchExternalJobs(extSearch, e.target.value); }}
                    className="h-9 rounded-lg border border-gray-200 px-3 text-xs bg-white"
                    data-testid="ext-source-filter"
                  >
                    <option value="all">All Sources</option>
                    {externalSources.filter(s => s.configured).map(s => (
                      <option key={s.key} value={s.key}>{s.name}</option>
                    ))}
                  </select>
                  <Button
                    size="sm"
                    className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs"
                    onClick={() => fetchExternalJobs(extSearch, extSourceFilter)}
                    data-testid="ext-search-btn"
                  >
                    <Search className="w-3.5 h-3.5 mr-1" /> Search
                  </Button>
                </div>
              </div>

              {/* Source Badges */}
              <div className="flex flex-wrap gap-2">
                {externalSources.map(s => (
                  <span key={s.key} className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-medium ${s.configured ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : 'bg-gray-50 text-gray-400 border border-gray-200'}`} data-testid={`ext-source-badge-${s.key}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${s.configured ? 'bg-emerald-500' : 'bg-gray-300'}`} />
                    {s.name} {!s.configured && '(key needed)'}
                  </span>
                ))}
              </div>

              {extLoading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
              ) : (
                <div className="space-y-3">
                  {externalJobs.length === 0 && <p className="text-center text-gray-400 py-8 bg-white rounded-lg border border-gray-200">No external jobs found. Try a different search or check source filters.</p>}
                  {externalJobs.map(job => (
                    <div key={job.id} className="bg-white border border-gray-200 rounded-lg p-5 hover-lift" data-testid={`ext-job-${job.id}`}>
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          {job.company_logo ? (
                            <img src={job.company_logo} alt="" className="w-10 h-10 rounded-lg object-contain border border-gray-100" />
                          ) : (
                            <div className="w-10 h-10 rounded-lg bg-gray-100 flex items-center justify-center"><Globe className="w-4 h-4 text-gray-400" /></div>
                          )}
                          <div>
                            <h3 className="text-sm font-bold text-gray-900">{job.title}</h3>
                            <p className="text-xs text-gray-500 flex items-center gap-2 mt-0.5">
                              <Building2 className="w-3 h-3" /> {job.company}
                              <MapPin className="w-3 h-3 ml-1" /> {job.location || 'Not specified'}
                              {job.remote && <span className="text-emerald-600 font-medium">Remote</span>}
                            </p>
                            {job.salary_text && <p className="text-[10px] text-gray-400 mt-0.5">{job.salary_text}</p>}
                          </div>
                        </div>
                        <div className="text-right flex flex-col items-end gap-1">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                            job.source === 'Remotive' ? 'bg-violet-100 text-violet-700' :
                            job.source === 'Arbeitnow' ? 'bg-sky-100 text-sky-700' :
                            job.source === 'USAJobs' ? 'bg-red-100 text-red-700' :
                            'bg-amber-100 text-amber-700'
                          }`} data-testid={`ext-source-tag-${job.id}`}>{job.source}</span>
                          <p className="text-lg font-bold text-emerald-600">${job.referral_bounty}<span className="text-[10px] text-gray-400 font-normal"> bounty</span></p>
                        </div>
                      </div>

                      {job.tags?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-2">
                          {job.tags.slice(0, 6).map((t, i) => <span key={`item-${i}`} className="text-[10px] bg-gray-100 px-2 py-0.5 rounded">{t}</span>)}
                        </div>
                      )}

                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-100">
                        <div className="flex items-center gap-3 text-[10px] text-gray-400">
                          {job.category && <span>{job.category}</span>}
                          {job.employment_type && <span className="capitalize">{job.employment_type}</span>}
                          {job.posted_date && <span>{new Date(job.posted_date).toLocaleDateString()}</span>}
                        </div>
                        <div className="flex gap-2">
                          {job.url && (
                            <a href={job.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-[#D4A843] hover:underline" data-testid={`ext-view-${job.id}`}>
                              <ExternalLink className="w-3 h-3" /> View
                            </a>
                          )}
                          <Button size="sm" className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" onClick={() => setShowExtRefer(showExtRefer === job.id ? null : job.id)} data-testid={`ext-refer-${job.id}`}>
                            <UserPlus className="w-3 h-3 mr-1" /> Refer & Earn ${job.referral_bounty}
                          </Button>
                        </div>
                      </div>

                      {/* External Refer Modal */}
                      {showExtRefer === job.id && (
                        <div className="mt-4 p-4 bg-gray-50 rounded-lg border border-gray-200 animate-fade-in" data-testid={`ext-refer-form-${job.id}`}>
                          <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3">Refer for: {job.title} @ {job.company}</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div><label className="text-xs text-gray-600">Name *</label><Input value={extReferForm.candidate_name} onChange={e => setExtReferForm({...extReferForm, candidate_name: e.target.value})} className="mt-1 rounded-lg" data-testid="ext-refer-name" /></div>
                            <div><label className="text-xs text-gray-600">Email *</label><Input value={extReferForm.candidate_email} onChange={e => setExtReferForm({...extReferForm, candidate_email: e.target.value})} className="mt-1 rounded-lg" data-testid="ext-refer-email" /></div>
                            <div><label className="text-xs text-gray-600">Phone</label><Input value={extReferForm.candidate_phone} onChange={e => setExtReferForm({...extReferForm, candidate_phone: e.target.value})} className="mt-1 rounded-lg" data-testid="ext-refer-phone" /></div>
                            <div><label className="text-xs text-gray-600">LinkedIn</label><Input value={extReferForm.candidate_linkedin} onChange={e => setExtReferForm({...extReferForm, candidate_linkedin: e.target.value})} className="mt-1 rounded-lg font-mono text-xs" data-testid="ext-refer-linkedin" /></div>
                            <div><label className="text-xs text-gray-600">Relationship</label><select value={extReferForm.relationship} onChange={e => setExtReferForm({...extReferForm, relationship: e.target.value})} className="mt-1 w-full h-9 rounded-lg border border-gray-200 px-3 text-xs bg-white" data-testid="ext-refer-relationship"><option value="">Select...</option><option value="colleague">Former Colleague</option><option value="friend">Friend</option><option value="network">Professional Network</option><option value="other">Other</option></select></div>
                            <div><label className="text-xs text-gray-600">Notes</label><Input value={extReferForm.notes} onChange={e => setExtReferForm({...extReferForm, notes: e.target.value})} className="mt-1 rounded-lg" placeholder="Why they're a good fit..." data-testid="ext-refer-notes" /></div>
                          </div>
                          <div className="flex gap-2 mt-3">
                            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs" onClick={() => submitExtReferral(job)} data-testid="ext-refer-submit"><Send className="w-3 h-3 mr-1" /> Submit Referral</Button>
                            <Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={() => setShowExtRefer(null)}>Cancel</Button>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── MY REFERRALS ── */}
          {view === 'referrals' && (
            <div className="space-y-3" data-testid="talent-referrals">
              {referrals.length === 0 && <p className="text-center text-gray-400 py-8 bg-white rounded-lg border border-gray-200">No referrals yet. Go to Job Board and refer candidates!</p>}
              {referrals.map(r => (
                <div key={r.id} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`talent-referral-${r.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold text-gray-900">{r.candidate_name} → {r.job_title}</p>
                      <p className="text-[10px] text-gray-400">{r.company} {r.is_external && <span className="text-violet-600 font-medium ml-1">External</span>} | Referred {new Date(r.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${STATUS_BADGE[r.status]}`}>{r.status}</span>
                      {r.referral_bounty > 0 && <span className="text-xs font-bold text-emerald-600">${r.referral_bounty.toLocaleString()}</span>}
                    </div>
                  </div>
                  {isAdmin && r.status === 'submitted' && (
                    <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                      <Button size="sm" className="bg-amber-500 text-white rounded-lg text-xs" onClick={() => updateReferralStatus(r.id, 'interviewing')}>Interviewing</Button>
                      <Button size="sm" className="bg-emerald-600 text-white rounded-lg text-xs" onClick={() => updateReferralStatus(r.id, 'hired')}>Hired</Button>
                      <Button size="sm" variant="outline" className="text-xs rounded-lg text-red-500" onClick={() => updateReferralStatus(r.id, 'rejected')}>Reject</Button>
                    </div>
                  )}
                  {user?.role === 'platform_admin' && r.status === 'hired' && !r.payout_status && (
                    <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                      <Button size="sm" className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" onClick={() => approveBountyPayout(r.id)} data-testid={`payout-btn-${r.id}`}><DollarSign className="w-3 h-3 mr-1" /> Approve Bounty Payout (${r.referral_bounty?.toLocaleString()})</Button>
                    </div>
                  )}
                  {r.payout_status && (
                    <p className="text-[10px] text-emerald-600 font-medium mt-2"><CheckCircle2 className="w-3 h-3 inline mr-1" />Payout {r.payout_status}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* ── EARNINGS ── */}
          {view === 'earnings' && earnings && (
            <div className="space-y-4" data-testid="talent-earnings">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-white border border-gray-200 rounded-lg p-5 text-center hover-lift"><DollarSign className="w-5 h-5 text-emerald-600 mx-auto mb-1" /><p className="text-2xl font-bold text-emerald-600">${earnings.total_earned.toLocaleString()}</p><p className="text-[10px] text-gray-500">Total Earned</p></div>
                <div className="bg-white border border-gray-200 rounded-lg p-5 text-center hover-lift"><p className="text-2xl font-bold text-[#D4A843]">${earnings.pending_potential.toLocaleString()}</p><p className="text-[10px] text-[#D4A843]">Pending Potential</p></div>
                <div className="bg-white border border-gray-200 rounded-lg p-5 text-center hover-lift"><p className="text-2xl font-bold text-gray-900">{earnings.total_referrals}</p><p className="text-[10px] text-gray-500">Total Referrals</p></div>
                <div className="bg-white border border-gray-200 rounded-lg p-5 text-center hover-lift"><p className="text-2xl font-bold text-amber-600">{earnings.conversion_rate}%</p><p className="text-[10px] text-amber-500">Conversion Rate</p></div>
              </div>
            </div>
          )}

          {/* ── PAYOUTS ── */}
          {view === 'payouts' && (
            <div className="space-y-4" data-testid="talent-payouts">
              <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Receipt className="w-4 h-4 text-emerald-600" /> Referral Bounty Payouts</h2>
              {payouts.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                  <DollarSign className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-400 text-sm">No payouts yet.</p>
                  <p className="text-gray-300 text-xs mt-1">Payouts are created when your referrals get hired!</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {payouts.map(p => (
                    <div key={p.id} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`payout-${p.id}`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-bold text-gray-900">{p.candidate_name} hired at {p.company}</p>
                          <p className="text-[10px] text-gray-400">{p.job_title} | Referrer: {p.referrer_name} ({p.referrer_email})</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${p.status === 'paid' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>{p.status}</span>
                          <span className="text-lg font-bold text-emerald-600">${p.amount?.toLocaleString()}</span>
                        </div>
                      </div>
                      <p className="text-[10px] text-gray-400 mt-1">{new Date(p.created_at).toLocaleDateString()}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* ── LEADERBOARD ── */}
          {view === 'leaderboard' && (
            <div className="space-y-4" data-testid="talent-leaderboard">
              {/* Period Filter */}
              <div className="flex items-center justify-between">
                <h2 className="text-sm font-bold text-gray-900 flex items-center gap-2"><Trophy className="w-4 h-4 text-amber-500" /> Referral Leaderboard</h2>
                <div className="flex gap-1">
                  {[{ k: 'month', l: 'This Month' }, { k: 'quarter', l: 'Quarter' }, { k: 'all', l: 'All Time' }].map(p => (
                    <button key={p.k} onClick={() => setLbPeriod(p.k)} className={`px-3 py-1.5 text-xs font-medium rounded-lg transition-colors ${lbPeriod === p.k ? 'bg-[#D4A843] text-[#0A0A0A] font-semibold' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`} data-testid={`lb-period-${p.k}`}>{p.l}</button>
                  ))}
                </div>
              </div>

              {lbLoading ? (
                <div className="flex items-center justify-center py-12"><Loader2 className="w-5 h-5 animate-spin text-gray-400" /></div>
              ) : leaderboard.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
                  <Trophy className="w-10 h-10 text-gray-300 mx-auto mb-3" />
                  <p className="text-gray-400 text-sm">No referrals yet for this period.</p>
                  <p className="text-gray-300 text-xs mt-1">Submit referrals to appear on the leaderboard!</p>
                </div>
              ) : (
                <>
                  {/* Top 3 Podium */}
                  {leaderboard.length >= 1 && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      {leaderboard.slice(0, 3).map((l, i) => {
                        const colors = [
                          { bg: 'bg-gradient-to-br from-amber-50 to-yellow-50', border: 'border-amber-300', icon: <Crown className="w-6 h-6 text-amber-500" />, badge: 'bg-amber-500', text: 'text-amber-700' },
                          { bg: 'bg-gradient-to-br from-gray-50 to-slate-50', border: 'border-gray-300', icon: <Medal className="w-5 h-5 text-gray-400" />, badge: 'bg-gray-400', text: 'text-gray-600' },
                          { bg: 'bg-gradient-to-br from-orange-50 to-amber-50', border: 'border-orange-300', icon: <Award className="w-5 h-5 text-orange-500" />, badge: 'bg-orange-400', text: 'text-orange-700' },
                        ][i];
                        return (
                          <div key={l.referrer_id} className={`${colors.bg} border ${colors.border} rounded-xl p-5 text-center relative overflow-hidden`} data-testid={`lb-top-${i + 1}`}>
                            <div className={`absolute top-2 left-2 w-7 h-7 ${colors.badge} text-white rounded-full flex items-center justify-center text-xs font-bold`}>#{i + 1}</div>
                            {colors.icon}
                            <p className={`text-sm font-bold mt-2 ${colors.text}`}>{l.referrer_name || 'Anonymous'}</p>
                            <p className="text-[10px] text-gray-400 mt-0.5">{l.referrer_email}</p>
                            <div className="mt-3 space-y-1">
                              <p className="text-2xl font-bold text-emerald-600">${l.total_earned.toLocaleString()}</p>
                              <p className="text-[10px] text-gray-500">earned from {l.hired} hire{l.hired !== 1 ? 's' : ''}</p>
                            </div>
                            <div className="flex justify-center gap-3 mt-3 pt-3 border-t border-gray-200/50">
                              <div><p className="text-sm font-bold text-gray-900">{l.total_referrals}</p><p className="text-[10px] text-gray-400">Referrals</p></div>
                              <div><p className="text-sm font-bold text-amber-600">{l.conversion_rate}%</p><p className="text-[10px] text-gray-400">Conv Rate</p></div>
                              <div><p className="text-sm font-bold text-[#D4A843]">${l.pending_potential.toLocaleString()}</p><p className="text-[10px] text-gray-400">Pending</p></div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Rest of Leaderboard Table */}
                  {leaderboard.length > 3 && (
                    <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                      <table className="w-full">
                        <thead><tr className="bg-gray-50 border-b border-gray-200">
                          <th className="text-left text-[10px] font-bold text-gray-500 uppercase px-4 py-3 w-12">Rank</th>
                          <th className="text-left text-[10px] font-bold text-gray-500 uppercase px-4 py-3">Referrer</th>
                          <th className="text-center text-[10px] font-bold text-gray-500 uppercase px-4 py-3">Referrals</th>
                          <th className="text-center text-[10px] font-bold text-gray-500 uppercase px-4 py-3">Hires</th>
                          <th className="text-center text-[10px] font-bold text-gray-500 uppercase px-4 py-3">Conv %</th>
                          <th className="text-right text-[10px] font-bold text-gray-500 uppercase px-4 py-3">Earned</th>
                          <th className="text-right text-[10px] font-bold text-gray-500 uppercase px-4 py-3">Pending</th>
                        </tr></thead>
                        <tbody>
                          {leaderboard.slice(3).map(l => (
                            <tr key={l.referrer_id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors" data-testid={`lb-row-${l.rank}`}>
                              <td className="px-4 py-3 text-sm font-bold text-gray-400">#{l.rank}</td>
                              <td className="px-4 py-3"><p className="text-sm font-medium text-gray-900">{l.referrer_name || 'Anonymous'}</p><p className="text-[10px] text-gray-400">{l.referrer_email}</p></td>
                              <td className="px-4 py-3 text-center text-sm text-gray-900">{l.total_referrals}</td>
                              <td className="px-4 py-3 text-center text-sm font-bold text-emerald-600">{l.hired}</td>
                              <td className="px-4 py-3 text-center text-sm text-amber-600">{l.conversion_rate}%</td>
                              <td className="px-4 py-3 text-right text-sm font-bold text-emerald-600">${l.total_earned.toLocaleString()}</td>
                              <td className="px-4 py-3 text-right text-sm text-[#D4A843]">${l.pending_potential.toLocaleString()}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {/* ── ENGAGEMENTS (Admin) ── */}
          {view === 'engagements' && isAdmin && (
            <div className="space-y-4" data-testid="talent-engagements">
              <Button onClick={() => setShowEngagement(!showEngagement)} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="talent-add-engagement"><Plus className="w-3.5 h-3.5 mr-1" /> Track Engagement</Button>
              {showEngagement && (
                <div className="bg-white border border-gray-200 rounded-lg p-6 space-y-3 animate-fade-in" data-testid="talent-engagement-form">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <div><label className="text-xs font-medium text-gray-700">Freelancer Name *</label><Input value={engForm.freelancer_name} onChange={e => setEngForm({...engForm, freelancer_name: e.target.value})} className="mt-1 rounded-lg" data-testid="eng-name" /></div>
                    <div><label className="text-xs font-medium text-gray-700">Email</label><Input value={engForm.freelancer_email} onChange={e => setEngForm({...engForm, freelancer_email: e.target.value})} className="mt-1 rounded-lg" data-testid="eng-email" /></div>
                    <div><label className="text-xs font-medium text-gray-700">Role</label><Input value={engForm.role} onChange={e => setEngForm({...engForm, role: e.target.value})} className="mt-1 rounded-lg" data-testid="eng-role" /></div>
                    <div><label className="text-xs font-medium text-gray-700">Hourly Rate ($)</label><Input type="number" value={engForm.hourly_rate} onChange={e => setEngForm({...engForm, hourly_rate: e.target.value})} className="mt-1 rounded-lg" data-testid="eng-rate" /></div>
                    <div><label className="text-xs font-medium text-gray-700">Business Name</label><Input value={engForm.business_name} onChange={e => setEngForm({...engForm, business_name: e.target.value})} className="mt-1 rounded-lg" data-testid="eng-biz" /></div>
                  </div>
                  <Button onClick={createEngagement} className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" data-testid="eng-submit">Track Engagement</Button>
                </div>
              )}
              <div className="space-y-3">
                {engagements.map(e => (
                  <div key={e.id} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`talent-eng-${e.id}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-bold text-gray-900">{e.freelancer_name}</p>
                        <p className="text-[10px] text-gray-400">{e.role} at {e.business_name} | ${e.hourly_rate}/hr | Since {e.start_date}</p>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${STATUS_BADGE[e.status]}`}>{e.status}</span>
                        {e.conversion_eligible && (
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs" onClick={() => setShowHireModal(e.id)} data-testid={`talent-hire-btn-${e.id}`}>
                            <UserPlus className="w-3 h-3 mr-1" /> Hire Full-Time
                          </Button>
                        )}
                      </div>
                    </div>
                    {showHireModal === e.id && (
                      <div className="mt-4 p-4 bg-amber-50 rounded-lg border border-amber-200 animate-fade-in" data-testid={`talent-hire-modal-${e.id}`}>
                        <h4 className="text-xs font-bold text-gray-900 uppercase tracking-wider mb-3 flex items-center gap-2"><Calculator className="w-3.5 h-3.5" /> Conversion Fee Calculator</h4>
                        <div className="grid grid-cols-2 gap-3">
                          <div><label className="text-xs text-gray-600">Proposed Annual Salary ($)</label><Input type="number" value={hireForm.proposed_salary} onChange={e => { setHireForm({...hireForm, proposed_salary: e.target.value}); if (e.target.value) calculateFee(e.target.value); }} className="mt-1 rounded-lg" data-testid="hire-salary" /></div>
                          {feeCalc && (
                            <div className="bg-white rounded-lg p-3 border border-gray-200">
                              <p className="text-[10px] text-gray-500">Conversion Fee ({feeCalc.fee_pct}%)</p>
                              <p className="text-lg font-bold text-[#D4A843]">${feeCalc.fee_amount.toLocaleString()}</p>
                            </div>
                          )}
                        </div>
                        <p className="text-[10px] text-gray-400 mt-2">12-month conversion clause applies. Fee due upon hire confirmation.</p>
                        <div className="flex gap-2 mt-3">
                          <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-xs" onClick={() => requestHire(e.id)} data-testid="hire-submit">Submit Hire Request</Button>
                          <Button size="sm" variant="outline" className="text-xs rounded-lg" onClick={() => { setShowHireModal(null); setFeeCalc(null); }}>Cancel</Button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
                {engagements.length === 0 && <p className="text-center text-gray-400 py-8 bg-white rounded-lg border border-gray-200">No engagements tracked. Click "Track Engagement" to add freelancer assignments.</p>}
              </div>
            </div>
          )}

          {/* ── HIRE REQUESTS (Admin) ── */}
          {view === 'hire_requests' && isAdmin && (
            <div className="space-y-3" data-testid="talent-hire-requests">
              {hireRequests.map(hr => (
                <div key={hr.id} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`talent-hr-${hr.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm font-bold text-gray-900">{hr.freelancer_name} → {hr.business_name}</p>
                      <p className="text-[10px] text-gray-400">Salary: ${hr.proposed_salary?.toLocaleString()} | Fee: ${hr.fee_amount?.toLocaleString()} ({hr.fee_pct}%)</p>
                    </div>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${STATUS_BADGE[hr.status]}`}>{hr.status}</span>
                  </div>
                  {user?.role === 'platform_admin' && hr.status === 'pending' && (
                    <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                      <Button size="sm" className="bg-emerald-600 text-white rounded-lg text-xs" onClick={async () => { await api.put(`/api/talent/hire-requests/${hr.id}`, { status: 'approved' }); toast.success('Approved!'); fetchData(); }} data-testid={`hr-approve-${hr.id}`}>Approve</Button>
                      <Button size="sm" variant="outline" className="text-xs rounded-lg text-red-500" onClick={async () => { await api.put(`/api/talent/hire-requests/${hr.id}`, { status: 'rejected' }); fetchData(); }}>Reject</Button>
                    </div>
                  )}
                  {isAdmin && hr.status === 'approved' && !hr.payment_status && (
                    <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                      <Button size="sm" className="bg-[#D4A843] hover:bg-[#E8C972] text-[#0A0A0A] font-semibold rounded-lg text-xs" onClick={() => payConversionFee(hr.id)} data-testid={`hr-pay-${hr.id}`}><CreditCard className="w-3 h-3 mr-1" /> Pay Conversion Fee (${hr.fee_amount?.toLocaleString()})</Button>
                    </div>
                  )}
                  {hr.payment_status === 'paid' && (
                    <p className="text-[10px] text-emerald-600 font-medium mt-2"><CheckCircle2 className="w-3 h-3 inline mr-1" />Conversion fee paid</p>
                  )}
                </div>
              ))}
              {hireRequests.length === 0 && <p className="text-center text-gray-400 py-8 bg-white rounded-lg border border-gray-200">No hire requests yet.</p>}
            </div>
          )}

          {/* ── PLATFORM REVENUE (Admin only) ── */}
          {view === 'revenue' && revenue && (
            <div className="space-y-4" data-testid="talent-revenue">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-5 text-center"><p className="text-2xl font-bold text-emerald-700">${revenue.total_revenue.toLocaleString()}</p><p className="text-[10px] text-emerald-600">Total Revenue</p></div>
                <div className="bg-white border border-gray-200 rounded-lg p-5 text-center"><p className="text-2xl font-bold text-[#D4A843]">${revenue.referral_revenue.toLocaleString()}</p><p className="text-[10px] text-[#D4A843]">Referral Bounties</p></div>
                <div className="bg-white border border-gray-200 rounded-lg p-5 text-center"><p className="text-2xl font-bold text-amber-600">${revenue.conversion_revenue.toLocaleString()}</p><p className="text-[10px] text-amber-500">Conversion Fees</p></div>
                <div className="bg-white border border-gray-200 rounded-lg p-5 text-center"><p className="text-2xl font-bold text-gray-900">{revenue.default_fee_pct}%</p><p className="text-[10px] text-gray-500">Default Fee Rate</p></div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div className="bg-white border border-gray-200 rounded-lg p-4 text-center"><p className="text-lg font-bold text-gray-900">{revenue.active_jobs}</p><p className="text-[10px] text-gray-500">Active Jobs</p></div>
                <div className="bg-white border border-gray-200 rounded-lg p-4 text-center"><p className="text-lg font-bold text-gray-900">{revenue.total_referrals}</p><p className="text-[10px] text-gray-500">Total Referrals</p></div>
                <div className="bg-white border border-gray-200 rounded-lg p-4 text-center"><p className="text-lg font-bold text-gray-900">{revenue.total_engagements}</p><p className="text-[10px] text-gray-500">Engagements</p></div>
                <div className="bg-white border border-gray-200 rounded-lg p-4 text-center"><p className="text-lg font-bold text-gray-900">{revenue.total_hire_requests}</p><p className="text-[10px] text-gray-500">Hire Requests</p></div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
